/* eslint-disable max-len */
import {
  Component, Element, h, Prop, State,
} from '@stencil/core';
import { versionComponent } from '../../global/inject-dependencies';
import { responsive } from '../../global/utility/responsive/responsive';

// TODO: User slots for content
/**
 * Component that allows for the display of a quote with information regarding its author as well
 */
@Component({
  tag: 'ui-quotation',
  styleUrl: 'ui-quotation.scss',
  shadow: true,
})
export class UiQuotation {
  @Element() el: HTMLElement;

  /**
   * Shows the name of the person who the quote belongs to
   */
  @Prop() personName: string;

  /**
   * Shows the job of the person who the quote belongs to
   */
  @Prop() personDescription: string;

  /**
   * Shows an image of the person who the quote belongs to
   */
  @Prop() personImage: string;

  /**
   * Watermark with copyright
   */
  @Prop() copyright: string;

  @Prop() imgSrc: string;

  /**
   * Caption for the image that is put there for acessability purposes
   */
  @Prop() imgAlt: string;

  /**
   * Specify how the quotation should be positioned horizontally on the background-image.
   */
  @Prop() quotationPositionX: 'left' | 'right' = 'left';

  /**
   * Specify how the quotation should be positioned vertically on the background-image.
   */
  @Prop() quotationPositionY: 'top' | 'bottom' = 'top';

  /**
* Specifies the horizontal focal point of an <img> or <video> in case the ratio needs to be adjusted (`object-fit: cover`).
   */
  @Prop() imageFocalPointX?: 'left' | 'right' | 'center' = 'center';

  /**
* Specifies the vertical focal point of an <img> or <video> in case the ratio needs to be adjusted (`object-fit: cover`).
*/
  @Prop() imageFocalPointY?: 'top' | 'bottom' | 'center' = 'center';

  @Prop() variant: 'white';

  @State() content: string;

  @Prop() replace?: string;

  @Prop() sizes?: string;

  meta: HTMLElement;

  text: HTMLElement;

  componentWillLoad() {
    this.content = this.el.innerHTML.replace('<!---->', '');
  }

  private setupResponsiveClampingForQutationsWithImages = () => {
    if (this.imgSrc) {
      const resizeObserver = new ResizeObserver(() => { this.clamp(); });
      resizeObserver.observe(this.el);
    }
  };

  componentDidLoad() {
    responsive(this.el);
    this.setupResponsiveClampingForQutationsWithImages();
  }

  get availableSpace() {
    const padding = 37 * 2;
    const metaHeight = this.meta.scrollHeight;
    return this.el.scrollHeight - padding - metaHeight;
  }

  clamp() {
    this.el.innerHTML = this.content;
    while (this.text.scrollHeight >= this.availableSpace) {
      // break if text is already shortened as much as possible
      if (!(/\W*\s(\S)*$/).test(this.el.innerHTML)) {
        return;
      }
      const shortenedText = this.el.innerHTML.replace(/\W*\s(\S)*$/, '...');
      this.el.innerHTML = shortenedText.replace('<!---->', '');
    }
  }

  createMeta() {
    return <footer
      class="quotation__meta"
      ref={(el) => { this.meta = el; }}
      itemScope
      itemType="http://schema.org/Person"
    >
      {this.personImage
        && <ui-image
          class="quotation__image"
          src={this.personImage}
          itemProp="image"
          altText="Person Image"
          replace={this.replace}
          sizes={this.sizes}
          positionX={this.imageFocalPointX}
          positionY={this.imageFocalPointY}
        ></ui-image>
      }
      {(this.personName || this.personDescription) && <div class="quotation__subline">
        <p class="quotation__name" itemProp="name">{this.personName}</p>
        <p class="quotation__info" itemProp="jobTitle">{this.personDescription}</p>
      </div>}
    </footer>;
  }

  render() {
    return (
      <blockquote class={{
        quotation: true,
        'quotation--with-image': !!this.imgSrc,
        [`quotation--${this.quotationPositionX}`]: true,
        [`quotation--${this.quotationPositionY}`]: true,
        'quotation--white': this.variant === 'white',
      }}>
        <div class="quotation__wrapper">
          {this.imgSrc && <ui-image
            class="quotation__background-image"
            src={this.imgSrc}
            alt-text={this.imgAlt}
            positionX={this.imageFocalPointX}
            positionY={this.imageFocalPointY}
            replace={this.replace}
            sizes={this.sizes}
          ></ui-image>}
          <div class="quotation__container">
            <p class="quotation__text" ref={(el) => { this.text = el; }}>
              <slot></slot>
            </p>
            {this.createMeta()}
          </div>
        </div>
        {this.copyright && <div class="quotation__copyright">©{this.copyright}</div>}
      </blockquote>
    );
  }
}

versionComponent(UiQuotation);
